import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";

// ─── アニメーション付きボタン ─────────────────────────────────
interface AnimatedBtnProps {
  onClick?: () => void;
  className?: string;
  children: React.ReactNode;
  animType?: "pop" | "bounce" | "glow";
  disabled?: boolean;
  type?: "button" | "submit";
}

function AnimatedBtn({ onClick, className = "", children, animType = "pop", disabled }: AnimatedBtnProps) {
  const [anim, setAnim] = useState(false);

  const handleClick = useCallback(() => {
    if (disabled) return;
    setAnim(true);
    onClick?.();
  }, [disabled, onClick]);

  const animClass =
    animType === "bounce" ? "animate-btn-bounce" :
    animType === "glow"   ? "animate-glow-pulse" :
    "animate-btn-pop";

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      onAnimationEnd={() => setAnim(false)}
      className={`${className} ${anim ? animClass : ""} transition-all active:scale-95`}
    >
      {children}
    </button>
  );
}

export default function Navbar() {
  const { user, openAuthModal, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [coinAnim, setCoinAnim] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // コイン残高が変わったらアニメーション
  const prevCoins = user?.coins;
  useEffect(() => {
    if (prevCoins !== undefined) {
      setCoinAnim(true);
      const t = setTimeout(() => setCoinAnim(false), 600);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.coins]);

  const navBg = scrolled
    ? isDark
      ? "bg-[#0a0a0f]/95 backdrop-blur-md shadow-lg"
      : "bg-white/95 backdrop-blur-md shadow-md"
    : "bg-transparent";

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
      <div className="flex items-center justify-between px-6 py-4">
        {/* Left: Site Name */}
        <Link to="/" className="flex items-center gap-2 cursor-pointer group">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-earth-line text-2xl text-amber-400 group-hover:text-amber-300 transition-all group-hover:rotate-12 duration-300"></i>
          </div>
          <span className={`text-xl font-black tracking-wide transition-colors font-['Space_Grotesk',sans-serif] whitespace-nowrap ${isDark ? "text-white group-hover:text-amber-300" : "text-gray-900 group-hover:text-amber-500"}`}>
            Buy the World
          </span>
        </Link>

        {/* Center Nav Links */}
        <div className="hidden md:flex items-center gap-8">
          {[
            { to: "/", label: lang === "ja" ? "ホーム" : "Home" },
            { to: "/map", label: t("mapTitle") },
            { to: "/auction", label: "オークション" },
            { to: "/ranking", label: "ランキング" },
            { to: "/stock-market", label: "株式市場" },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`text-sm font-medium transition-all hover:text-amber-400 hover:-translate-y-0.5 whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-600"}`}
            >
              {item.label}
            </Link>
          ))}
          <button
            onClick={() => setLang(lang === "ja" ? "en" : "ja")}
            className={`text-sm font-medium transition-all hover:text-amber-400 hover:scale-105 whitespace-nowrap cursor-pointer ${isDark ? "text-gray-300" : "text-gray-600"}`}
          >
            {lang === "ja" ? "🇯🇵 日本語" : "🇺🇸 English"}
          </button>
          {/* Theme toggle */}
          <AnimatedBtn
            onClick={toggleTheme}
            animType="pop"
            className={`w-8 h-8 flex items-center justify-center rounded-full transition-all cursor-pointer ${isDark ? "bg-white/5 hover:bg-white/10 text-gray-300" : "bg-gray-100 hover:bg-gray-200 text-gray-600"}`}
          >
            <i className={isDark ? "ri-sun-line text-sm" : "ri-moon-line text-sm"}></i>
          </AnimatedBtn>
        </div>

        {/* Right: Auth */}
        <div className="flex items-center gap-3">
          {user ? (
            <div className="relative">
              <AnimatedBtn
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                animType="pop"
                className="flex items-center gap-2 bg-amber-400/10 hover:bg-amber-400/20 border border-amber-400/30 rounded-full px-4 py-2 cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-user-3-line text-amber-400 text-base"></i>
                </div>
                <span className="text-amber-400 text-sm font-medium whitespace-nowrap">{user.username}</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-coin-line text-amber-400 text-xs ${coinAnim ? "animate-star-pop" : ""}`}></i>
                </div>
                <span className={`text-amber-300 text-xs whitespace-nowrap font-bold ${coinAnim ? "animate-success-burst" : ""}`}>
                  {user.coins.toLocaleString()}
                </span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-amber-400 text-sm transition-transform duration-200 ${userMenuOpen ? "rotate-180" : ""}`}></i>
                </div>
              </AnimatedBtn>

              {userMenuOpen && (
                <div className={`absolute right-0 top-full mt-2 w-48 border rounded-xl overflow-hidden shadow-2xl animate-slide-up-fade ${isDark ? "bg-[#0f1015] border-white/10" : "bg-white border-gray-200"}`}>
                  <button
                    onClick={() => { navigate("/mypage"); setUserMenuOpen(false); }}
                    className={`w-full text-left px-4 py-3 text-sm transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap hover:translate-x-1 ${isDark ? "text-gray-300 hover:bg-white/5 hover:text-amber-400" : "text-gray-700 hover:bg-gray-50 hover:text-amber-500"}`}
                  >
                    <i className="ri-user-3-line text-amber-400"></i>
                    マイページ
                  </button>
                  <button
                    onClick={() => { navigate("/map"); setUserMenuOpen(false); }}
                    className={`w-full text-left px-4 py-3 text-sm transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap hover:translate-x-1 ${isDark ? "text-gray-300 hover:bg-white/5 hover:text-amber-400" : "text-gray-700 hover:bg-gray-50 hover:text-amber-500"}`}
                  >
                    <i className="ri-map-line text-amber-400"></i>
                    {t("navMyLand")}
                  </button>
                  <div className={`border-t ${isDark ? "border-white/10" : "border-gray-100"}`}></div>
                  <button
                    onClick={() => { logout(); setUserMenuOpen(false); }}
                    className={`w-full text-left px-4 py-3 text-sm transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap hover:translate-x-1 ${isDark ? "text-gray-300 hover:bg-white/5 hover:text-red-400" : "text-gray-700 hover:bg-gray-50 hover:text-red-500"}`}
                  >
                    <i className="ri-logout-box-line text-red-400"></i>
                    {t("navLogout")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <AnimatedBtn
                onClick={() => openAuthModal("login")}
                animType="pop"
                className={`hidden md:inline-flex items-center text-sm font-medium transition-all border rounded-full px-5 py-2 whitespace-nowrap cursor-pointer ${isDark ? "text-gray-300 hover:text-white border-white/20 hover:border-white/50" : "text-gray-600 hover:text-gray-900 border-gray-300 hover:border-gray-500"}`}
              >
                {t("navLogin")}
              </AnimatedBtn>
              <AnimatedBtn
                onClick={() => openAuthModal("register")}
                animType="bounce"
                className="bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-black text-sm font-bold rounded-full px-5 py-2 whitespace-nowrap cursor-pointer shadow-lg shadow-amber-500/20"
              >
                {t("navRegister")}
              </AnimatedBtn>
            </>
          )}

          {/* Mobile menu */}
          <AnimatedBtn
            onClick={() => setMenuOpen(!menuOpen)}
            animType="pop"
            className={`md:hidden cursor-pointer ${isDark ? "text-gray-300" : "text-gray-700"}`}
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl transition-all`}></i>
            </div>
          </AnimatedBtn>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className={`md:hidden border-t px-6 py-4 space-y-4 animate-slide-up-fade ${isDark ? "bg-[#0a0a0f]/98 border-white/10" : "bg-white border-gray-200"}`}>
          <Link to="/" className={`block text-sm font-medium transition-colors cursor-pointer hover:text-amber-400 ${isDark ? "text-gray-300" : "text-gray-700"}`} onClick={() => setMenuOpen(false)}>
            {lang === "ja" ? "ホーム" : "Home"}
          </Link>
          <Link to="/map" className={`block text-sm font-medium transition-colors cursor-pointer hover:text-amber-400 ${isDark ? "text-gray-300" : "text-gray-700"}`} onClick={() => setMenuOpen(false)}>
            {t("mapTitle")}
          </Link>
          <button onClick={() => { setLang(lang === "ja" ? "en" : "ja"); setMenuOpen(false); }}
            className={`block text-sm font-medium transition-colors cursor-pointer hover:text-amber-400 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
            {lang === "ja" ? "🇯🇵 日本語" : "🇺🇸 English"}
          </button>
          <button onClick={() => { toggleTheme(); setMenuOpen(false); }}
            className={`flex items-center gap-2 text-sm font-medium transition-colors cursor-pointer hover:text-amber-400 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
            <i className={isDark ? "ri-sun-line text-amber-400" : "ri-moon-line text-gray-600"}></i>
            {isDark ? (lang === "ja" ? "ライトモード" : "Light Mode") : (lang === "ja" ? "ダークモード" : "Dark Mode")}
          </button>
          {!user && (
            <button onClick={() => { openAuthModal("login"); setMenuOpen(false); }}
              className={`block text-sm font-medium transition-colors cursor-pointer hover:text-amber-400 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              {t("navLogin")}
            </button>
          )}
        </div>
      )}
    </nav>
  );
}
